'use client';

import {RecaptchaScript} from '@gfiber/third-parties/google';
import {zodResolver} from '@hookform/resolvers/zod';
import {Typography} from '@pixel-party/Typography/v2';
import {useParams} from 'next/navigation';
import type React from 'react';
import {useId} from 'react';
import {Controller, type SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSubmit} from '@/app/forms/_hooks/useSubmit';
import {REGISTRY} from '@/app/forms/_registry/client';
import * as constants from '@/app/forms/constants';
import {NO, YES_NO_MAP} from '@/app/forms/i18n';
import {ApiError, type InferFormValues} from '@/app/forms/types';
import {FileUpload, FormError, Radio, TextArea, TextField} from '@/components';
import {isRequired, maxLength} from '@/lib/utils/common';
import {
  getRootError,
  getSchemaShape,
  getSfdcLanguage,
  handleError,
  onSubmitError,
  tSafe,
} from '../../utils';
import {Consent} from '../Consent/Consent';
import {Description, FormDescription} from '../Description/Description';
import {SubmitButton} from '../SubmitButton/SubmitButton';
import {SuccessMessage} from '../SuccessMessage/SuccessMessage';
import styles from './AddressVerificationForm.module.scss';
import {VerificationDocuments} from './VerificationDocuments';

function AddressVerificationForm(): React.JSX.Element {
  const {t} = useTranslation();

  const config = REGISTRY.AddressVerificationForm;
  const params = useParams();
  type AddressVerificationFormData = InferFormValues<typeof config>;

  const {
    control,
    formState: {errors, isSubmitted, isValid},
    handleSubmit,
    setError,
  } = useForm<AddressVerificationFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      forcedCancellation: NO,
      billingAddress: '',
      ticketId: '',
      verificationDocuments: [],
      formType: 'Address_Verification',
      language: getSfdcLanguage(params?.locale),
    },
    mode: 'onChange',
    resolver: zodResolver(config.schema),
  });
  const fields = getSchemaShape(config.schema);
  const {mutate, isPending, isSuccess} = useSubmit<AddressVerificationFormData>(
    config.id,
  );
  const id = useId();

  const onSubmit: SubmitHandler<AddressVerificationFormData> = async data => {
    const recaptchaToken = await window.grecaptcha?.enterprise.execute(
      constants.RECAPTCHA_SITE_KEY,
      {action: config.recaptchaAction || 'submit_form'},
    );

    if (!recaptchaToken) {
      handleError(
        new ApiError(t('errors.recaptcha'), {
          code: 'RECAPTCHA_FAILED',
          message: 'reCAPTCHA token could not be obtained',
        }),
        setError,
        fields,
      );
      return;
    }

    mutate(
      {data, recaptchaToken},
      {
        onError: error => {
          handleError(error, setError, fields);
        },
      },
    );
  };

  return (
    <div className={styles.container}>
      <h1 className="title">{t(config.title)}</h1>
      {isSuccess ? (
        <SuccessMessage />
      ) : (
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
          noValidate
        >
          <fieldset disabled={isPending}>
            <FormDescription>
              <Description>
                {t('forms.addressVerification.description')}
              </Description>
            </FormDescription>
            <Typography
              use="headline2"
              className={styles.headline}
            >
              {t('forms.addressVerification.contactInformation.headline')}
            </Typography>
            <div className="field">
              <Controller
                control={control}
                name="firstName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t(
                      'forms.addressVerification.contactInformation.firstName',
                    )}
                    id={`firstName-${id}`}
                    required={isRequired(fields.firstName)}
                    errorText={tSafe(errors.firstName?.message, t)}
                    maxLength={maxLength(fields.firstName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="lastName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t(
                      'forms.addressVerification.contactInformation.lastName',
                    )}
                    id={`lastName-${id}`}
                    required={isRequired(fields.lastName)}
                    errorText={tSafe(errors.lastName?.message, t)}
                    maxLength={maxLength(fields.lastName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="email"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="email"
                    label={t(
                      'forms.addressVerification.contactInformation.email',
                    )}
                    id={`email-${id}`}
                    required={isRequired(fields.email)}
                    helpText={t(
                      'forms.addressVerification.contactInformation.emailHelp',
                    )}
                    errorText={tSafe(errors.email?.message, t)}
                    maxLength={maxLength(fields.email)}
                  />
                )}
              />
            </div>
            <fieldset className="field">
              <legend>
                <Typography use="body2">
                  {t('forms.addressVerification.forcedCancellation')}:{' '}
                  {isRequired(fields.forcedCancellation) && '*'}
                </Typography>
              </legend>
              <Controller
                name="forcedCancellation"
                control={control}
                render={({field: {onChange, value, ref}}) => (
                  <div
                    ref={ref}
                    className="radioGroup"
                  >
                    {YES_NO_MAP.map(item => (
                      <Radio
                        key={item.id}
                        id={`forcedCancellation-${item.id}`}
                        name="forcedCancellation"
                        value={item.value}
                        label={t(item.i18nKey, {defaultValue: item.id})}
                        checked={value === item.value}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              />
              <FormError
                message={tSafe(errors.forcedCancellation?.message, t)}
              />
            </fieldset>
            <div className="field">
              <Controller
                control={control}
                name="billingAddress"
                render={({field}) => (
                  <TextArea
                    {...field}
                    label={t('forms.addressVerification.billingAddress')}
                    rows={2}
                    id={`billingAddress-${id}`}
                    required={isRequired(fields.billingAddress)}
                    helpText={t('forms.addressVerification.billingAddressHelp')}
                    errorText={tSafe(errors.billingAddress?.message, t)}
                    maxLength={maxLength(fields.billingAddress)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="ticketId"
                render={({field}) => (
                  <TextArea
                    {...field}
                    label={t('forms.addressVerification.ticketId')}
                    rows={2}
                    id={`ticketId-${id}`}
                    required={isRequired(fields.ticketId)}
                    errorText={tSafe(errors.ticketId?.message, t)}
                    maxLength={maxLength(fields.ticketId)}
                  />
                )}
              />
            </div>
            <div className="field">
              <VerificationDocuments />
            </div>
            <div className="field">
              <Controller
                name="verificationDocuments"
                control={control}
                render={({field}) => (
                  <>
                    <FileUpload
                      value={field.value ?? []}
                      instruction={t(
                        'forms.addressVerification.verificationDocuments.instruction',
                      )}
                      buttonLabel={t('fileUpload.buttonLabel')}
                      defaultFileName={t('fileUpload.defaultFileName')}
                      fileAttachedLabel={t('fileUpload.fileAttachedLabel')}
                      maxFileSizeErrorMessage={t('errors.fileTooBig', {
                        size: constants.MAX_FILE_SIZE_MB,
                      })}
                      fileTypeErrorMessage={t('errors.invalidFileType')}
                      accept={constants.ACCEPTED_FILE_TYPES_STRING}
                      maxFiles={5}
                      onFileSelect={(_id, files) => field.onChange(files)}
                      required={isRequired(fields.verificationDocuments)}
                    />
                    <FormError
                      message={tSafe(errors.verificationDocuments?.message, t)}
                    />
                  </>
                )}
              />
            </div>
            <SubmitButton
              isDisabled={isPending}
              isPending={isPending}
              rootError={getRootError(errors, isSubmitted, isValid, t)}
            />
            <Consent />
          </fieldset>
          {constants.RECAPTCHA_SITE_KEY && (
            <RecaptchaScript siteKey={constants.RECAPTCHA_SITE_KEY} />
          )}
        </form>
      )}
    </div>
  );
}

export {AddressVerificationForm};
